import { readonly, ref, type Ref } from 'vue';
import { useNativeEvent } from './useNativeEvent';

export interface VisibilityOptions {
	/** Appelé à chaque retour au premier plan. */
	onVisible?: () => void;
	/** Appelé au passage en arrière-plan. */
	onHidden?: () => void;
}

/**
 * Visibilité de l'application.
 *
 * L'application chauffeur et la borne restent ouvertes des heures : le système
 * les met en veille, et des événements temps réel se perdent. Le retour au
 * premier plan est donc le moment naturel pour resynchroniser.
 *
 * `taxi:resume` est aussi écouté : c'est l'événement émis par le pont natif,
 * `visibilitychange` n'étant pas toujours déclenché dans une WebView.
 */
export function useVisibility(options: VisibilityOptions = {}): Readonly<Ref<boolean>> {
	const visible = ref(typeof document === 'undefined' ? true : !document.hidden);

	const sync = (): void => {
		const isVisible = !document.hidden;
		if (isVisible === visible.value) {
			return;
		}
		visible.value = isVisible;
		(isVisible ? options.onVisible : options.onHidden)?.();
	};

	useNativeEvent(document, 'visibilitychange', sync);
	useNativeEvent(document, 'taxi:resume', () => options.onVisible?.());

	return readonly(visible);
}
